import { create } from 'zustand';
import { supabase, checkIsAdmin, getUserProfile } from '../utils/supabase';

const useAuthStore = create((set, get) => ({
  user: null,
  profile: null,
  isAdmin: false,
  session: null,
  loading: true,
  initialized: false,

  setUser: (user) => set({ user }),
  setProfile: (profile) => set({ profile, isAdmin: profile?.role === 'admin' }),
  setIsAdmin: (isAdmin) => set({ isAdmin }),
  setSession: (session) => set({ session }),
  setLoading: (loading) => set({ loading }),

  // Initialize auth and check for existing session
  initialize: async () => {
    try {
      set({ loading: true });

      // Check for existing session
      const { data: { session } } = await supabase.auth.getSession();

      if (session) {
        set({ session, user: session.user });

        // Fetch user profile to get role
        const profile = await getUserProfile(session.user.id);
        if (profile) {
          set({ profile, isAdmin: profile.role === 'admin' });
        }
      }

      // Listen for auth changes
      supabase.auth.onAuthStateChange(async (event, session) => {
        if (session) {
          set({ session, user: session.user });
          const profile = await getUserProfile(session.user.id);
          if (profile) {
            set({ profile, isAdmin: profile.role === 'admin' });
          }
        } else {
          set({ session: null, user: null, profile: null, isAdmin: false });
        }
      });

      set({ initialized: true, loading: false });
    } catch (error) {
      console.error('Error initializing auth:', error);
      set({ initialized: true, loading: false });
    }
  },

  // Sign in with email and password
  signIn: async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      set({ session: data.session, user: data.user });

      // Fetch profile
      const profile = await getUserProfile(data.user.id);
      if (profile) {
        set({ profile, isAdmin: profile.role === 'admin' });
      }

      return { success: true };
    } catch (error) {
      console.error('Sign in error:', error);
      return { success: false, error: error.message };
    }
  },

  // Sign out
  signOut: async () => {
    try {
      await supabase.auth.signOut();
      set({
        user: null,
        profile: null,
        isAdmin: false,
        session: null,
      });
      return { success: true };
    } catch (error) {
      console.error('Sign out error:', error);
      return { success: false, error: error.message };
    }
  },

  // Refresh admin status
  refreshAdminStatus: async () => {
    const isAdmin = await checkIsAdmin();
    set({ isAdmin });
    return isAdmin;
  },
}));

export default useAuthStore;
